<template>
  <div class="h-[100dvh] overflow-hidden flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-2 sm:p-4">
    <!-- Logo -->
    <div class="mb-2 sm:mb-4 text-center flex flex-col items-center">
      <div class="flex items-center justify-center">
        <img 
          src="@/assets/logo/logo-jean.jpg" 
          alt="Jean Informatica Logo" 
          class="w-64 sm:w-72 md:w-96 h-auto max-h-[15vh] object-contain"
          @error="handleImageError"
        />
      </div>
    </div>

    <!-- Formulario de Login -->
    <div class="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
      <div class="p-4 sm:p-6">
        <div class="mb-2 sm:mb-4 text-center">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">
            Iniciar Sesión
          </h2>
          <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1">
            Ingresa tus credenciales para continuar
          </p>
        </div>

        <form @submit.prevent="handleLogin">
          <div class="mb-3 sm:mb-4">
            <label for="username" class="block mb-1 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Usuario o Email
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                id="username"
                v-model="form.username"
                @input="error = ''"
                type="text"
                required
                class="pl-10 w-full px-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors text-sm"
                placeholder="Ingresa tu usuario o email"
              />
            </div>
          </div>

          <div class="mb-3 sm:mb-4">
            <label for="password" class="block mb-1 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Contraseña
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                id="password"
                v-model="form.password"
                @input="error = ''"
                :type="showPassword ? 'text' : 'password'"
                required
                class="pl-10 pr-10 w-full px-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors text-sm"
                placeholder="Ingresa tu contraseña"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg v-if="!showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between mb-3 sm:mb-4">
            <div class="flex items-center">
              <input
                id="remember"
                v-model="form.remember"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="remember" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Recordarme
              </label>
            </div>
            <button
              type="button"
              @click="$router.push('/forgot-password')"
              class="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 sm:py-2.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="!loading">Iniciar Sesión</span>
            <span v-else>Iniciando...</span>
          </button>
          
          <div v-if="error" class="mt-3 text-center">
            <div class="p-3 rounded-lg" :class="errorType === 'inactive' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'">
              <p class="text-sm" :class="errorType === 'inactive' ? 'text-yellow-700 dark:text-yellow-300' : 'text-red-600 dark:text-red-400'">
                {{ error }}
              </p>
            </div>
          </div>
        </form>
      </div>

      <div class="px-5 py-2 sm:px-8 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          ¿No tienes una cuenta?
          <button
            type="button"
            @click="$router.push('/register')"
            class="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Regístrate aquí
          </button>
        </p>
      </div>
    </div>

    <div class="mt-6 text-center animate-fade-in" style="animation-delay: 0.5s;">
      <div class="flex flex-col items-center gap-2">
        <div class="flex items-center gap-2 text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-[0.2em] font-bold">
          <span>Plataforma de Gestión</span>
          <span class="w-1 h-1 rounded-full bg-slate-300 dark:bg-gray-700"></span>
          <span>v1.0.0</span>
        </div>
        <div class="flex items-center justify-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
          <span class="opacity-80">Desarrollado con</span>
          <svg class="w-3.5 h-3.5 text-rose-500 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <span class="opacity-80">por</span>
          <span class="font-black text-indigo-600 dark:text-indigo-400 tracking-tight">Jean Informático C.A.</span>
        </div>
      </div>
    </div>

    <button
      @click="toggleDarkMode"
      class="fixed top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-shadow"
      title="Cambiar tema"
    >
      <svg v-if="darkMode" class="h-6 w-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
      <svg v-else class="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    </button>

    <!-- MODAL PERSONALIZADO DE SELECCIÓN DE EMPRESAS -->
    <div 
      v-if="mostrandoSeleccionEmpresa" 
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(8px);"
      @click.self="cerrarSelectorEmpresas"
    >
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-modal-in">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-gray-800 dark:text-white">
                Selecciona tu empresa
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Tienes acceso a múltiples empresas
              </p>
            </div>
          </div>
          <button
            @click="cerrarSelectorEmpresas"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-4 space-y-2">
          <div
            v-for="(empresa, index) in empresasDisponibles"
            :key="index"
            @click="seleccionarEmpresa(empresa)"
            class="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-all group"
          >
            <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {{ (empresa.nombre || 'E').charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-gray-800 dark:text-white truncate">
                {{ empresa.nombre || 'Empresa' }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400 truncate">
                ID: {{ empresa.tenantId || empresa.id || 'N/A' }}
              </p>
            </div>
            <svg class="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        <div class="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            @click="cerrarSelectorEmpresas"
            class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loginUsuario, limpiarEmpresas } from '@/api/usuarios.js';
import { setAuth, clearAuth, isAuthenticated, getCurrentTenant, setCurrentTenant } from '@/api/index.js';
import { cargarConfigTenant, limpiarConfigTenant } from '@/utils/tenantConfig';

export default {
  name: 'LoginPage',
  data() {
    return {
      form: {
        username: '',
        password: '',
        remember: false
      },
      showPassword: false,
      loading: false,
      error: '',
      errorType: 'general',
      darkMode: false,
      logoError: false,
      mostrandoSeleccionEmpresa: false,
      empresasDisponibles: [],
      resolviendoSeleccion: null
    };
  },
  mounted() {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    this.darkMode = savedDarkMode;
    this.applyDarkMode(savedDarkMode);

    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      this.form.username = savedUsername;
      this.form.remember = true;
    }
    
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (userData && token && isAuthenticated()) {
      setTimeout(() => {
        this.$router.push('/bienvenida');
      }, 100);
    }
    
    const tenant = getCurrentTenant();
    if (!tenant || tenant === 'Jean Informática C.A.' || tenant.includes(' ') || tenant.length > 50) {
      console.warn('⚠️ Tenant inválido detectado, reseteando a default');
      setCurrentTenant('default');
    }
  },
  methods: {
    // ============================================================
    // HANDLE LOGIN CORREGIDO
    // ============================================================
    async handleLogin() {
      this.error = '';
      this.errorType = 'general';
      this.mostrandoSeleccionEmpresa = false;
      
      if (!this.form.username.trim()) {
        this.error = 'Por favor ingresa tu usuario o email';
        this.errorType = 'general';
        return;
      }
      
      if (!this.form.password) {
        this.error = 'Por favor ingresa tu contraseña';
        this.errorType = 'general';
        return;
      }

      this.loading = true;

      try {
        let tenantId = getCurrentTenant();
        
        if (!tenantId || tenantId === 'Jean Informática C.A.' || 
            tenantId.includes(' ') || tenantId.length > 50) {
          console.warn('⚠️ Tenant inválido, usando default:', tenantId);
          tenantId = 'default';
          setCurrentTenant(tenantId);
        }
        
        console.log('🏢 Tenant a usar:', tenantId);

        // ✅ PRIMER LOGIN (sin selección confirmada)
        const response = await loginUsuario(
          this.form.username, 
          this.form.password, 
          tenantId, 
          false
        );

        // ✅ CASO 1: Múltiples empresas
        if (response.success && response.requiereSeleccionEmpresa === true) {
          console.log('🏢 Usuario con múltiples empresas, mostrando selector');
          console.log('📋 EMPRESAS:', JSON.stringify(response.empresasDisponibles, null, 2));
          
          this.empresasDisponibles = response.empresasDisponibles || [];
          this.mostrandoSeleccionEmpresa = true;
          this.loading = false;
          
          const seleccionada = await new Promise((resolve) => {
            this.resolviendoSeleccion = resolve;
          });
          
          if (!seleccionada) {
            this.loading = false;
            this.mostrandoSeleccionEmpresa = false;
            return;
          }

          console.log('🏢 Empresa seleccionada:', seleccionada);
          this.loading = true;
          this.mostrandoSeleccionEmpresa = false;
          
          // ✅ SEGUNDO LOGIN (empresa ya confirmada)
          const response2 = await loginUsuario(
            this.form.username, 
            this.form.password, 
            seleccionada, 
            true
          );
          
          if (response2.success && response2.data) {
            await this.procesarLoginExitoso(response2.data);
          } else {
            this.handleLoginError(response2);
          }
          return;
        }

        // ✅ CASO 2: Login exitoso (una sola empresa)
        if (response.success && response.data) {
          await this.procesarLoginExitoso(response.data);
        } else {
          this.handleLoginError(response);
        }
        
      } catch (error) {
        console.error('❌ Error en login:', error);
        this.handleLoginError(error);
      } finally {
        if (!this.mostrandoSeleccionEmpresa) {
          this.loading = false;
        }
      }
    },

    // ============================================================
    // SELECCIONAR EMPRESA (CORREGIDO)
    // ============================================================
    seleccionarEmpresa(empresa) {
      console.log('📋 Empresa seleccionada (raw):', empresa);
      
      // 🔥 El tenantId SIEMPRE debe venir del backend
      let tenantId = empresa.tenantId || empresa.TenantId || null;
      
      if (!tenantId) {
        if (empresa.nombre === 'Jean Informática C.A.') {
          tenantId = 'default';
        } else {
          console.warn('⚠️ Empresa sin tenantId, usando default');
          tenantId = 'default';
        }
      }
      
      console.log('✅ Tenant ID final:', tenantId);
      
      if (this.resolviendoSeleccion) {
        this.resolviendoSeleccion(tenantId);
        this.resolviendoSeleccion = null;
      }
    },

    // ============================================================
    // CERRAR SELECTOR
    // ============================================================
    cerrarSelectorEmpresas() {
      console.log('❌ Selección cancelada');
      if (this.resolviendoSeleccion) {
        this.resolviendoSeleccion(null);
        this.resolviendoSeleccion = null;
      }
      this.mostrandoSeleccionEmpresa = false;
    },

    // ============================================================
    // PROCESAR LOGIN EXITOSO
    // ============================================================
    async procesarLoginExitoso(userData) {
      let tenantToSave = 'default';
      if (userData.empresaSeleccionada) {
        if (userData.empresaSeleccionada.tenantId) {
          tenantToSave = userData.empresaSeleccionada.tenantId;
        } else if (userData.empresaSeleccionada.id) {
          tenantToSave = userData.empresaSeleccionada.id;
        }
      }
      
      if (tenantToSave.includes(' ') || tenantToSave === 'Jean Informática C.A.') {
        tenantToSave = 'default';
      }
      
      setCurrentTenant(tenantToSave);
      console.log('🏢 Tenant guardado:', tenantToSave);
      
      const userToStore = {
        idUsuario: userData.idUsuario,
        nombreUsuario: userData.nombreUsuario,
        nombre: userData.nombre,
        apellido: userData.apellido,
        email: userData.email,
        telefono: userData.telefono || '',
        idRol: userData.rol?.idRol,
        nombreRol: userData.rol?.nombre,
        token: userData.token,
        tenantId: tenantToSave,
        timestamp: new Date().toISOString()
      };
      
      setAuth(userData.token, userToStore, this.form.remember);
      
      if (this.form.remember) {
        localStorage.setItem('rememberedUsername', this.form.username);
      }

      localStorage.setItem('tenantId', tenantToSave);
      
      console.log(`✅ Bienvenido ${userData.nombre}!`);

      try {
        const config = await cargarConfigTenant(tenantToSave);
        if (config) {
          console.log('✅ Config del tenant cargada:', config);
        } else {
          console.log('⚠️ No hay config visual para el tenant:', tenantToSave);
        }
      } catch (err) {
        console.warn('Error cargando config del tenant:', err);
      }

      window.dispatchEvent(new CustomEvent('userLoggedIn', {
        detail: { tenantId: tenantToSave }
      }));
      
      setTimeout(() => {
        this.$router.push('/bienvenida');
      }, 500);
    },

    // ============================================================
    // MANEJO DE ERRORES
    // ============================================================
    handleLoginError(response) {
      const errorMessage = response.error || response.message || 'Credenciales incorrectas';
      this.error = errorMessage;
      
      const errorType = response.type || 'general';
      
      if (errorType === 'inactive' || errorMessage.toLowerCase().includes('inactivo')) {
        this.errorType = 'inactive';
      } else if (errorType === 'password' || errorMessage.toLowerCase().includes('contraseña')) {
        this.errorType = 'password';
      } else if (errorType === 'notfound' || errorMessage.toLowerCase().includes('no encontrado')) {
        this.errorType = 'notfound';
      } else if (errorType === 'network' || errorMessage.toLowerCase().includes('conexión')) {
        this.errorType = 'network';
      } else if (errorType === 'tenant_error' || errorMessage.toLowerCase().includes('empresa')) {
        this.errorType = 'tenant_error';
        setCurrentTenant('default');
      }
      
      clearAuth();
    },

    // ============================================================
    // UTILIDADES
    // ============================================================
    clearSessionData() {
      clearAuth();
      limpiarEmpresas();
      setCurrentTenant('default');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantConfig');
      window.dispatchEvent(new CustomEvent('userLoggedOut'));
    },

    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      this.applyDarkMode(this.darkMode);
      localStorage.setItem('darkMode', this.darkMode.toString());
    },

    applyDarkMode(isDark) {
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    },

    handleImageError() {
      this.logoError = true;
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.95) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-out forwards;
}

.animate-modal-in {
  animation: modalIn 0.3s ease-out forwards;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.dark ::-webkit-scrollbar-track {
  background: #1f2937;
}

.dark ::-webkit-scrollbar-thumb {
  background: #4b5563;
}
</style>